<template>
  <div class="tenants">
    <div class="container view">
      <HeaderComponent :title="$tc('account', 2)" />
      <p class="tenants__subtitle">{{ $t('switchTenantSubtitle') }}</p>

      <div v-if="loading" class="tenants__loading">
        {{ $t('loading', { resource: $tc('account', 2).toLowerCase() }) }}...
      </div>

      <EmptyState
        v-else-if="!tenants.length"
        :title="$t('noX', { x: $tc('account', 2).toLowerCase() })"
        :icon="['fal', 'building']"
      />

      <template v-else>
        <div v-if="mainAccount" class="tenants__main">
          <div class="tenants__main-info">
            <span class="tenants__main-title">{{ mainAccount.title }}</span>
            <Badge>{{ $t('mainAccount') }}</Badge>
          </div>
          <ButtonComponent
            theme="primary"
            :label="$t('goTo')"
            :icon="['fal', 'arrow-right']"
            icon-right
            @click="navigate(mainAccount.url)"
          />
        </div>

        <div v-if="otherAccounts.length" class="tenants__list">
          <div class="tenants__list-header">
            <span>{{ $tc('account', 1) }}</span>
            <span>{{ otherAccounts.length }} {{ $tc('account', 2).toLowerCase() }}</span>
          </div>
          <div class="tenants__grid">
            <div v-for="tenant in otherAccounts" :key="tenant.url" class="tenants__item">
              <span class="tenants__item-title">{{ tenant.title }}</span>
              <ButtonComponent
                theme="secondary"
                :label="$t('goTo')"
                :icon="['fal', 'arrow-right']"
                icon-right
                @click="navigate(tenant.url)"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import TenantAPI from '@/api/Tenant'
import HeaderComponent from '@/components/Header'
import Badge from '@/components/Badge'
import EmptyState from '@/components/EmptyState'

export default {
  components: {
    HeaderComponent,
    Badge,
    EmptyState,
  },

  data() {
    return {
      tenants: [],
      loading: true,
    }
  },

  computed: {
    ...mapState(['user']),

    mainAccount() {
      if (!this.user || !this.user.email) return
      
      const { email } = this.user
      const [username, domain] = email.split('@')
      const domainSlug = (domain || '').split('.')[0]

      const firstSegment = url => {
        try {
          return new URL(url).hostname.split('.')[0]
        } catch {
          return ''
        }
      }

      return (
        this.tenants.find(t => firstSegment(t.url) === domainSlug) ||
        this.tenants.find(t => firstSegment(t.url) === username)
      )
    },

    otherAccounts() {
      return this.tenants.filter(t => t !== this.mainAccount)
    },
  },

  methods: {
    navigate(url) {
      window.location.href = url
    },
  },

  async created() {
    try {
      this.tenants = await TenantAPI.getAll()
    } finally {
      this.loading = false
    }
  },

  metaInfo() {
    return {
      title: this.$tc('account', 2),
    }
  },
}
</script>

<style lang="scss">
.tenants {
  background-color: $background-grey;
  min-height: 100%;

  &__subtitle {
    margin-top: -0.5rem;
    margin-bottom: 1.5rem;
    opacity: 0.6;
    font-size: 0.9rem;
  }

  &__loading {
    padding: 2rem 0;
    opacity: 0.6;
  }

  &__main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: white;
    border-radius: $border-radius;
    padding: 1.25rem 1.5rem;
    margin-bottom: 2rem;
    box-shadow: $box-shadow;
  }

  &__main-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__main-title {
    font-weight: 600;
    font-size: 1rem;
  }

  &__list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    opacity: 0.7;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    background: white;
    border-radius: $border-radius;
    box-shadow: $box-shadow;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-right: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);

    &:nth-child(even) {
      border-right: none;
    }
  }
}
</style>
